import { Grid, TextField, InputLabel } from '@material-ui/core'
import React, { useState, useEffect } from 'react'

import { getTranslate } from '../../localization'
import useStyles from './styles/TimeDurationInput.styles'

// Define the shape of the initial values
interface TimeValues {
  day: number
  hour: number
  minute: number
  second: number
}

// Function to parse formatted time string and return a TimeValues object
export const parseFormattedTimeString = (timeString: string): TimeValues => {
  const timeValues: TimeValues = {
    day: 0,
    hour: 0,
    minute: 0,
    second: 0
  }

  const regex = /(\d+d)|(\d+h)|(\d+m)|(\d+s)/g
  const matches = timeString.match(regex)

  if (matches) {
    matches.forEach((match) => {
      const unit = match.slice(-1)
      const value = parseInt(match.slice(0, -1))

      switch (unit) {
        case 'd':
          timeValues.day = value
          break
        case 'h':
          timeValues.hour = value
          break
        case 'm':
          timeValues.minute = value
          break
        case 's':
          timeValues.second = value
          break
        default:
          break
      }
    })
  }

  return timeValues
}
// Helper function to format time values to a string like '1d 3h 34m 21s'
const formatTimeValues = (timeValues: TimeValues): string => {
  const { day, hour, minute, second } = timeValues
  let formattedTime = ''
  if (day > 0) formattedTime += `${day}d `
  if (hour > 0) formattedTime += `${hour}h `
  if (minute > 0) formattedTime += `${minute}m `
  if (second > 0) formattedTime += `${second}s`
  return formattedTime.trim()
}
// Define the props interface
interface TimeDurationInputProps {
  initialValues: string // Initial values for day, hour, minute, and second
  onTimeChange: (formattedTime: string) => void // Callback to return the formatted time string
  label: string // Label for the input group
  loading: boolean
}

const TimeDurationInput: React.FC<TimeDurationInputProps> = ({
  initialValues,
  onTimeChange,
  label,
  loading
}) => {
  const classes = useStyles()

  const [timeValues, setTimeValues] = useState<TimeValues>(
    parseFormattedTimeString(initialValues)
  )

  useEffect(() => {
    if (!loading) setTimeValues(parseFormattedTimeString(initialValues))
  }, [initialValues, loading])

  // Debounce or throttle to prevent too frequent updates
  useEffect(() => {
    const formattedTime = formatTimeValues(timeValues)
    onTimeChange(formattedTime)
  }, [timeValues])

  const handleInputChange = (field: keyof TimeValues, value: string) => {
    setTimeValues((prev) => ({
      ...prev,
      [field]: parseInt(value, 10) || 0
    }))
    // const formattedTime = formatTimeValues(timeValues)
    // onTimeChange(formattedTime)
  }

  return (
    <div className={classes.root}>
      <InputLabel className={classes.label}>{getTranslate(label)}</InputLabel>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TextField
            InputProps={{
              classes: { notchedOutline: classes.noFocusOutline }
            }}
            label={getTranslate('Day')}
            type="number"
            placeholder={getTranslate('Day')}
            value={timeValues.day}
            onChange={(event) => handleInputChange('day', event.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
              classes: { root: classes.greyTextFieldLabel }
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            InputProps={{
              classes: { notchedOutline: classes.noFocusOutline }
            }}
            label={getTranslate('Hour')}
            type="number"
            placeholder={getTranslate('Hour')}
            value={timeValues.hour}
            onChange={(event) => handleInputChange('hour', event.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
              classes: { root: classes.greyTextFieldLabel }
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            InputProps={{
              classes: { notchedOutline: classes.noFocusOutline }
            }}
            label={getTranslate('Minute')}
            type="number"
            placeholder={getTranslate('Minute')}
            value={timeValues.minute}
            onChange={(event) =>
              handleInputChange('minute', event.target.value)
            }
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
              classes: { root: classes.greyTextFieldLabel }
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            InputProps={{
              classes: { notchedOutline: classes.noFocusOutline }
            }}
            label={getTranslate('Second ')}
            type="number"
            placeholder={getTranslate('Second')}
            value={timeValues.second}
            onChange={(event) =>
              handleInputChange('second', event.target.value)
            }
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
              classes: { root: classes.greyTextFieldLabel }
            }}
          />
        </Grid>
      </Grid>
    </div>
  )
}

export default TimeDurationInput
